/**
 * The label pair — bundled because MuiInputLabel extends MuiFormLabel
 * and their styling is intertwined.
 *
 *   MuiFormLabel  — used for STANDALONE labels above a field (e.g.,
 *                   <FormControl><FormLabel htmlFor="email">Email</FormLabel>
 *                   <TextField placeholder="…" /></FormControl>). See
 *                   SignInCard / ProfilePage. Small caption above the
 *                   field, nothing more.
 *
 *                   NOTE: MuiInputLabel extends this. Anything set here
 *                   bleeds into every TextField's floating label. Keep
 *                   this rule to pure typography + spacing so it stays
 *                   safe for both surfaces. Decorative bits (pill
 *                   background, transform) live under MuiInputLabel.
 *
 *   MuiInputLabel — the FLOATING label rendered by TextField. Two jobs:
 *
 *     1. Pill-cutout background trick — the label sits on top of the
 *        input's border with a solid background matching the input's
 *        background, so the border appears "broken" where the label
 *        overlaps it. This replaces MUI's default <fieldset>+<legend>
 *        notched-outline pattern (which the theme disables via
 *        notchedOutline: { border: 'none' } up in TextInput.jsx).
 *
 *     2. Resting-state transform — MuiOutlinedInput puts padding on the
 *        *root* and zeroes the *input's* internal padding. MUI's default
 *        resting transforms (translate(14px, 16px) medium / (14px, 9px)
 *        small) were calibrated for MUI's default geometry. Under this
 *        theme's geometry, MUI's defaults land the label off-center —
 *        most visibly on size="medium" where it drifts 5-6px low.
 *        Re-center explicitly.
 *
 *   marginBottom is reset to 0 on MuiInputLabel because MuiFormLabel
 *   sets it to 12 for standalone use — that value has no meaning on an
 *   absolutely-positioned InputLabel and would leak into layout math
 *   if anything downstream did read it.
 */
export const labelCustomizations = {
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        typography: theme.typography.caption,
        marginBottom: 12,
      }),
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        marginBottom: 0,
        padding: '0 6px',
        display: 'inline-flex',
        alignItems: 'center',
        zIndex: 1,
        backgroundColor: theme.palette.background.default,
        borderRadius: theme.shape.borderRadius / 2,
        ...theme.applyStyles('dark', {
          backgroundColor: theme.palette.background.paper,
        }),
        variants: [
          {
            props: { variant: 'outlined' },
            style: {
              '&:not(.MuiInputLabel-shrink)': {
                transform: 'translate(14px, 10px) scale(1)',
              },
            },
          },
          {
            props: { variant: 'outlined', size: 'small' },
            style: {
              '&:not(.MuiInputLabel-shrink)': {
                transform: 'translate(14px, 8px) scale(1)',
              },
            },
          },
        ],
      }),
    },
  },
};
